import { Router } from "@angular/router";
import { Injectable } from "@angular/core";
import { Location } from "@angular/common";
import { tap, map } from "rxjs/operators";
import { createEffect, Actions, ofType } from "@ngrx/effects";

import * as fromActions from "@app/core/store/feature-actions";
import { FeatureService } from '../services/feature/feature.service';
/**
 * Common shared NGRX actions to handle navigation between screens generated by `UI Studio` tool.
 *
 * Please refer to our official documentation for more informations about the supported screens:
 * https://wiki.vermeg.com/display/PFD/Components+Store#ComponentsStore-Screens
 *
 * This class should not be modified.
 */
@Injectable()
export class FeatureEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private location: Location,
    private featureService: FeatureService
  ) {}
  /**
   * The effect that clears the current route and then opens the target screen
   * using auxiliary Angular routes in a Popup dialog or above the caller component
   */
  navigateByCallOrDialogAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CALL, fromActions.DIALOG),
        map(payload => payload),
        tap((payload: any) => {
          this.router
            .navigate(
              [
                `./`,
                {
                  outlets: {
                    call: null,
                    dialog: null
                  }
                }
              ],
              {
                relativeTo: payload.activeRoute
              }
            )
            .then(() => {
              this.router.navigate(
                [
                  `./`,
                  {
                    outlets: {
                      [payload["navigationType"]]: [
                        payload["feature"],
                        payload["screen"],
                        ...this.getPathParams(payload)
                      ]
                    }
                  }
                ],
                {
                  queryParams: {
                    fc: crypto.getRandomValues(new Uint32Array(1)),
                    ctx: this.featureService.getQueryScreenId(payload["ctx"]),
                    ...this.getQueryParams(payload)
                  },
                  relativeTo: payload.activeRoute,
                  replaceUrl:
                    payload["type"] === fromActions.CALL ? true : false
                }
              );
            });
        })
      ),
    { dispatch: false }
  );
  /**
   * The effect that opens the target screen in a new page
   */
  navigateBySwitchOrForardAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.SWITCH, fromActions.FORWARD),
        map(payload => payload),
        tap((payload: any) => {
          this.router.navigate(
            [`/${payload.workSpaceId}/${payload.path}`, ...this.getPathParams(payload)],
            {
              queryParams: {
                fc: crypto.getRandomValues(new Uint32Array(1)),
                ctx: this.featureService.getQueryScreenId(payload["ctx"]),
                ...this.getQueryParams(payload)
              },
              replaceUrl: payload["type"] === fromActions.FORWARD ? true : false
            }
          );
        })
      ),
    { dispatch: false }
  );
  /**
   * The effect that displays the previous caller page
   */
  navigateBackAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.BACK),
        map(payload => payload),
        tap(payload => {
          this.location.back();
        })
      ),
    { dispatch: false }
  );
  /**
   * Gets the shared query parameters
   * @param payload
   */
  private getQueryParams(payload: any) {
    return payload["queryParams"]
      ? { ...payload["queryParams"] }
      : {};
  }
  /**
   * Gets the path parameter that is mainly the identifier of the data object to access
   * @param payload
   */
  private getPathParams(payload: any) {
    return payload["pathParams"]
      ? [...payload["pathParams"]].filter(item => item)
      : [];
  }
}
